import { useCallback, useContext, useRef, useState } from 'react';

import type { Vacancy, VacancyWithIndeedJob } from 'clients/AutoCompleteClient/types';

import { logger } from 'config/logger';
import { queryClient } from 'config/queryClient';
import { useAutoProcessAtsVacancy } from 'hooks/shared/useAutoProcessAtsVacancy';
import { useAutoProcessIndeedVacancy } from 'hooks/shared/useAutoProcessIndeedVacancy';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { useCreateSnackbar } from 'shared/legacy/contexts/SnackbarContext/useCreateSnackbar';
import { useRobinJrStore } from 'stores/robin-jr/robin-jr.store';
import { QueryKey } from 'types/query';
import { parseJobImprovedErrorMessage } from 'utils/jobImprovedFieldMapper';
import useFeatureToggles from './useFeatureToggles';

export const useRobinJrSelectVacancy = ({ clearErrors }: { clearErrors?: () => void } = {}) => {
  const [tempVacancy, setTempVacancy] = useState<Vacancy | null>(null);

  const { dictionary } = useContext(LocalizationContext);
  const createSnackbar = useCreateSnackbar();

  const selectedVacancy = useRobinJrStore((s) => s.selectedVacancy);
  const selectedVacancyActiveType = useRobinJrStore((s) => s.selectedVacancyActiveType);
  const setSelectedVacancyActiveType = useRobinJrStore((s) => s.setSelectedVacancyActiveType);
  const setSelectedVacancyRobinJr = useRobinJrStore((s) => s.setSelectedVacancy);
  const setHasInsufficientInfoError = useRobinJrStore((s) => s.setHasInsufficientInfoError);

  const { isFeatureToggleEnabled } = useFeatureToggles();
  const isJobPreviewFeatureEnabled = isFeatureToggleEnabled((f) => f.RJ_COMMUNICATION_HUB);

  const { mutateAsync: autoProcessIndeedVacancy } = useAutoProcessIndeedVacancy();
  const { mutateAsync: autoProcessAtsVacancy } = useAutoProcessAtsVacancy();
  const isProcessingVacancyRef = useRef(false);

  const handleAutoProcessAtsVacancy = useCallback(
    async (vacancy: Vacancy) => {
      if (isProcessingVacancyRef.current) return;

      isProcessingVacancyRef.current = true;

      try {
        const processedAtsVacancy = await autoProcessAtsVacancy({ atsVacancy: vacancy });

        setSelectedVacancyRobinJr(processedAtsVacancy);
      } catch (error) {
        logger.error(error);

        createSnackbar(parseJobImprovedErrorMessage(error, dictionary), {
          variant: 'danger',
          duration: 10000,
        });

        setSelectedVacancyRobinJr(vacancy);
      } finally {
        setTempVacancy(null);
        isProcessingVacancyRef.current = false;
      }
    },
    [autoProcessAtsVacancy, setSelectedVacancyRobinJr, createSnackbar, dictionary],
  );

  const handleAutoProcessIndeedVacancy = useCallback(
    async (vacancy: VacancyWithIndeedJob) => {
      if (isProcessingVacancyRef.current) return;

      try {
        const processedIndeedVacancy = await autoProcessIndeedVacancy(vacancy);

        setSelectedVacancyRobinJr(processedIndeedVacancy);
      } catch (error) {
        logger.error(error);

        createSnackbar(parseJobImprovedErrorMessage(error, dictionary), {
          variant: 'danger',
          duration: 10000,
        });

        setSelectedVacancyRobinJr(vacancy);
      } finally {
        setTempVacancy(null);
        isProcessingVacancyRef.current = false;
      }
    },
    [autoProcessIndeedVacancy, setSelectedVacancyRobinJr, createSnackbar, dictionary],
  );

  const handleSelectVacancy = useCallback(
    (vacancy: Vacancy | null) => {
      clearErrors?.();
      setHasInsufficientInfoError(false);

      const newVacancyInstance: Vacancy | null = vacancy ? { ...vacancy } : null;

      if (newVacancyInstance?.type === 'ATS_RAW') {
        setTempVacancy(newVacancyInstance);
        handleAutoProcessAtsVacancy(newVacancyInstance);
        return;
      }

      if (newVacancyInstance?.type === 'INDEED' && !newVacancyInstance.is_improved && isJobPreviewFeatureEnabled) {
        setTempVacancy(newVacancyInstance);
        handleAutoProcessIndeedVacancy(newVacancyInstance);
        return;
      }

      if (newVacancyInstance?.is_improved) {
        queryClient.invalidateQueries([QueryKey.useEnrichedVacanciesQuery, { search: '' }], { exact: false });
      }

      setSelectedVacancyRobinJr(vacancy);
    },
    [
      handleAutoProcessAtsVacancy,
      handleAutoProcessIndeedVacancy,
      clearErrors,
      setHasInsufficientInfoError,
      setSelectedVacancyRobinJr,
      isJobPreviewFeatureEnabled,
    ],
  );

  return {
    selected: tempVacancy ?? selectedVacancy ?? null,
    activeType: selectedVacancyActiveType,
    setSelected: handleSelectVacancy,
    setSelectedType: setSelectedVacancyActiveType,
    isProcessingVacancy: tempVacancy !== null,
  };
};
